import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Plus, RefreshCw, Info } from 'lucide-react';
import { useExperiment } from './hooks/useExperiment';
import { FunnelTable, VariantsList, VariantDialog, ClonePlansDialog } from './components';
import { ExperimentPageProps } from './types';

const formatMoney = (value: number) =>
  new Intl.NumberFormat('uz-UZ', {
    style: 'currency',
    currency: 'UZS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

export default function ExperimentPage({ isDark }: ExperimentPageProps) {
  const {
    variants,
    analytics,
    isLoading,
    isSubmitting,
    isVariantDialogOpen,
    editingVariant,
    variantForm,
    setVariantForm,
    openCreateVariant,
    openEditVariant,
    setIsVariantDialogOpen,
    submitVariant,
    isCloneDialogOpen,
    cloningVariant,
    cloneForm,
    setCloneForm,
    openClonePlans,
    setIsCloneDialogOpen,
    submitClone,
    activate,
    stopEnrollment,
    archive,
    remove,
    refresh,
  } = useExperiment();

  const hasActive = variants.some((v) => v.status === 'active');

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            className={cn(
              'text-2xl lg:text-3xl font-bold tracking-tight',
              isDark ? 'text-white' : 'text-[#111111]',
            )}
          >
            Эксперимент
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            A/B-тест прайсинга: разным людям — разные цены, решение по ARPU
            {analytics?.days_running !== null && analytics?.days_running !== undefined
              ? ` · идёт ${analytics.days_running} дн.`
              : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={refresh}
            disabled={isLoading}
            className={cn('rounded-xl', isDark ? 'border-slate-700 text-slate-300' : '')}
          >
            <RefreshCw className={cn('w-4 h-4 mr-2', isLoading && 'animate-spin')} />
            Обновить
          </Button>
          <Button onClick={openCreateVariant} className="bg-[#C9A227] hover:bg-[#C9A227]/90 rounded-xl">
            <Plus className="w-4 h-4 mr-2" />
            Создать вариант
          </Button>
        </div>
      </div>

      {/* Состояние эксперимента */}
      {!hasActive && (
        <div
          className={cn(
            'flex items-start gap-3 rounded-2xl px-4 py-3 text-sm',
            isDark ? 'bg-slate-800/60 text-slate-300' : 'bg-slate-100 text-slate-600',
          )}
        >
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <p>
            Эксперимент выключен: активных вариантов нет, и все пользователи видят
            базовый прайс-лист — ровно то же, что и раньше. Тест начнётся в момент,
            когда вы нажмёте «Запустить набор». Назначения выдаются только тем, кто
            зарегистрировался после первого запуска.
          </p>
        </div>
      )}

      {/* Общая воронка «заказ → оплата»: если отвал здесь, ценовой тест бессмыслен */}
      {analytics && analytics.totals.orders_count > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Назначено в тест', value: String(analytics.totals.assigned) },
            { label: 'Заказов создано', value: String(analytics.totals.orders_count) },
            {
              label: 'Заказ → оплата',
              value: `${analytics.totals.order_to_payment_rate}%`,
              hint: 'Если отвал здесь большой — проблема в платёжке, а не в цене',
            },
            { label: 'Выручка теста', value: formatMoney(analytics.totals.revenue) },
          ].map((kpi) => (
            <Card
              key={kpi.label}
              className={cn(
                'rounded-2xl border-0 p-4',
                isDark ? 'bg-[#1A1A22] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
              )}
            >
              <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>{kpi.label}</p>
              <p className={cn('text-2xl font-bold mt-1', isDark ? 'text-white' : 'text-[#111111]')}>
                {kpi.value}
              </p>
              {kpi.hint && (
                <p className={cn('text-xs mt-1', isDark ? 'text-slate-500' : 'text-slate-400')}>{kpi.hint}</p>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Воронка по вариантам */}
      <Card
        className={cn(
          'rounded-2xl border-0 overflow-hidden transition-all duration-300',
          isDark ? 'bg-[#1A1A22] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
        )}
      >
        <div className={cn('px-5 pt-5', isDark ? 'text-white' : 'text-[#111111]')}>
          <h2 className="font-semibold">Воронка по вариантам</h2>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Решает ARPU, а не конверсия: дешёвый вариант почти всегда выигрывает по
            конверсии и при этом может проиграть по деньгам. Группа, где меньше{' '}
            {analytics?.min_payments_for_decision ?? 8} оплат, помечена — по ней решение
            не принимается.
          </p>
        </div>
        <FunnelTable isDark={isDark} analytics={analytics} isLoading={isLoading} />
      </Card>

      {/* Варианты */}
      <VariantsList
        isDark={isDark}
        variants={variants}
        onEdit={openEditVariant}
        onClonePlans={openClonePlans}
        onActivate={activate}
        onStopEnrollment={stopEnrollment}
        onArchive={archive}
        onDelete={remove}
      />

      <VariantDialog
        isDark={isDark}
        isOpen={isVariantDialogOpen}
        onOpenChange={setIsVariantDialogOpen}
        formData={variantForm}
        onFormDataChange={setVariantForm}
        onSubmit={submitVariant}
        isSubmitting={isSubmitting}
        isEditing={Boolean(editingVariant)}
      />

      <ClonePlansDialog
        isDark={isDark}
        isOpen={isCloneDialogOpen}
        onOpenChange={setIsCloneDialogOpen}
        variant={cloningVariant}
        variants={variants}
        formData={cloneForm}
        onFormDataChange={setCloneForm}
        onSubmit={submitClone}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
